import type { ReactElement } from 'react';
import { cn } from '@/lib/utils';
import type { AlertDescriptionSections } from '@kirin/api-client/helpers/alerts';

interface AlertDescriptionProps {
  sections: AlertDescriptionSections | null;
  fallbackDescription: string;
  className?: string;
}

export function AlertDescription({
  sections,
  fallbackDescription,
  className,
}: AlertDescriptionProps): ReactElement {
  if (sections == null) {
    return (
      <p className={cn('break-words', className)}>{fallbackDescription}</p>
    );
  }

  const whatHappened = sections.whatHappened.trim();
  const whyItMatters = sections.whyItMatters.trim();
  const detected = sections.detected?.trim() ?? '';

  if (whatHappened.length === 0 && whyItMatters.length === 0) {
    return (
      <p className={cn('break-words', className)}>{fallbackDescription}</p>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {whatHappened.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            What happened
          </p>
          <p className="break-words">{whatHappened}</p>
        </div>
      )}
      {detected.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Detected
          </p>
          <p className="break-words font-mono" data-testid="alert-detected">
            {detected}
          </p>
        </div>
      )}
      {whyItMatters.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Why it matters
          </p>
          <p className="break-words">{whyItMatters}</p>
        </div>
      )}
    </div>
  );
}

AlertDescription.displayName = 'AlertDescription';
